<template>
  <div>
    <b-breadcrumb>
      <b-breadcrumb-item to="/">
        <i class="fa fa-home"></i>
        Home
      </b-breadcrumb-item>
      <b-breadcrumb-item to="/HumanResources/VISAs"
        >Human Resources</b-breadcrumb-item
      >
      <b-breadcrumb-item active>VISAs</b-breadcrumb-item>
    </b-breadcrumb>

    <div class="card">
      <div class="bg-dark card-header text-white">VISAs</div>
      <div class="card-body">
        <!-- The collapsable filter options -->
        <AppFormCardCollapsable :display-footer="true" @submit.prevent="onFilterSubmit()" class="mb-3">
          <template v-slot:header>Filter</template>
          <template v-slot:body>
            <div class="form-row">
              <div class="col-md-4 col-sm-6 form-group">
                <label>Name</label>
                <input
                        class="form-control"
                        placeholder="Name"
                        v-model="filterOptions.name"
                />
              </div>
              <div class="col-md-2 col-sm-6 form-group">
                <label>UIN</label>
                <input
                        class="form-control"
                        placeholder="UIN"
                        v-model="filterOptions.uin"
                />
              </div>
              <div class="col-md-2 col-sm-6 form-group">
                  <label>Supervisor Name</label>
                  <input
                    class="form-control"
                    placeholder="Supervisor Name"
                    v-model="filterOptions.supervisorName"
                  />
              </div>
              <div class="col-md-2 col-sm-6 form-group">
                <label>VISA Expiration Date</label>
                <b-input-group class="mb-3">
                  <b-form-input
                    v-model="visaExpirationDate"
                    type="text"
                    placeholder="MM/DD/YYYY"
                    :disabled="true"
                  ></b-form-input>
                  <b-input-group-append>
                    <b-form-datepicker
                      v-model="filterOptions.visaExpirationDate"
                      button-only
                      right
                      reset-button
                      locale="en-US"
                      aria-controls="visaExpirationDate"
                      @context="updateVisaExpirationDateFromButton()"
                    ></b-form-datepicker>
                  </b-input-group-append>
                </b-input-group>
              </div>
            </div>

            <div v-if="!readOnly" class="form-row">
              <div class="col-md-2 col-sm-6 form-group">
                <label>VISA Type</label>
                <b-form-select v-model="filterOptions.visaTypes" :options="visaTypes" multiple :select-size="4"></b-form-select>
              </div>
              <div class="col-md-2 col-sm-6 form-group">
                  <AppCheckBox
                          v-model="filterOptions.onlyHRProcessingVISA"
                  >Only Show Those Processing
                  </AppCheckBox>
              </div>
              <div class="col-md-2 col-sm-6 form-group">
                  <AppCheckBox
                          v-model="filterOptions.onlyHRApprovedI140"
                  >Only Show Approved I-140
                  </AppCheckBox>
              </div>
              <div class="col-md-2 col-sm-6 form-group">
                  <AppCheckBox
                          v-model="filterOptions.onlyShowActive"
                          @change="updateEmploymentStatus()"
                  >Only Show Currently Employed
                  </AppCheckBox>
              </div>
              <div class="col-md-2 col-sm-6 form-group">
                <label>Employment Type</label>
                <b-form-select v-model="filterOptions.employeeTypes" :options="employeeTypes" multiple :select-size="4"></b-form-select>
              </div>
              <div class="col-md-2 col-sm-6 form-group">
                  <AppCheckBox
                          v-model="filterOptions.includeStudents"
                  >Include Students
                  </AppCheckBox>
              </div>
              <div class="col-md-2 col-sm-6 form-group">
                  <AppCheckBox
                          v-model="filterOptions.excludeLawfulPermanentResidents"
                  >Exclude Lawful Permanent Residents
                  </AppCheckBox>
              </div>
            </div>

            <div v-if="!filterOptions.onlyShowActive" class="form-row">
              <div class="col-md-2">
                <label>Employement Status</label>
                <select class="form-control" v-model="filterOptions.employmentStatus">
                    <option :value="null">All</option>
                    <option :key="status.key" :value="status.value" v-for="status in employmentStatuses">{{status.key}}</option>
                </select>
              </div>
            </div>
          </template>
          <template v-slot:footer>
            <button class="btn btn-primary" type="submit">Apply</button>
          </template>
        </AppFormCardCollapsable>

        <div class="row">
          <div class="col d-print-none dropdown mb-3 text-right">
              <button :disabled="isPerformingAction" aria-expanded="false" aria-haspopup="true"
                      class="btn btn-secondary dropdown-toggle" data-toggle="dropdown" style="width: 100px"
                      type="button">
                  <span>Actions</span>
              </button>
              <div class="dropdown-menu">
                  <a @click.prevent="downloadCsvExport()" class="btn btn-outline-light dropdown-item text-left"
                    href="javascript:void(0)" title="Export VISA People List to a CSV">
                      <i class="fa fa-file-excel"></i>Download .csv
                  </a>
              </div>
          </div>
        </div>

        <BTable
          :busy="isLoading"
          :fields="tableFields"
          :items="visas"
          :responsive="true"
          primary-key="id"
          show-empty
          striped
          table-class="table-align-middle"
        >
          <template v-slot:cell(id)="row">
            <router-link
              :to="{
                name: 'VisaDetails',
                params: {
                  id: row.item.id,
                  personName: row.item.name,
                  personUIN: row.item.uin,
                },
              }"
              class="btn btn-sm btn-warning d-print-none"
              data-toggle="tooltip"
              title="Edit person details"
            >
              <i class="fa fa-pencil-alt"></i>Edit
            </router-link>
          </template>

          <template v-slot:cell(visaType)="row">
            <span>{{ row.item.visaType || "-" }}</span>
          </template>

          <template v-slot:cell(visaExpirationDate)="row">
            <span>{{
              row.item.visaExpirationDate
                ? new Date(row.item.visaExpirationDate).toLocaleDateString()
                : "-"
            }}</span>
          </template>

          <template v-slot:cell(h1BStartDate)="row">
            <span>{{
              row.item.h1BStartDate
                ? new Date(row.item.h1BStartDate).toLocaleDateString()
                : "-"
            }}</span>
          </template>

          <template v-slot:cell(hrProcessingVISA)="row">
              <AppDisplayTemplateBoolean :value="row.item.hrProcessingVISA"></AppDisplayTemplateBoolean>
          </template>

          <template v-slot:cell(hrApprovedI140)="row">
              <AppDisplayTemplateBoolean :value="row.item.hrApprovedI140"></AppDisplayTemplateBoolean>
          </template>

          <!-- Footer Pagination -->
          <template v-slot:custom-foot>
            <b-tr>
              <b-td :colspan="tableFields.length">
                <AppTablePagination
                  :page.sync="filterOptions.page"
                  :pageSize.sync="filterOptions.pageSize"
                  :totalCount.sync="totalVisas"
                  v-on:update:page="loadData()"
                  v-on:update:pageSize="loadData()"
                ></AppTablePagination>
              </b-td>
            </b-tr>
          </template>
        </BTable>
      </div>
    </div>
  </div>
</template>

<script>
import AppTablePagination from "../../components/shared/AppTablePagination";
import PersonVisaService from "@/services/PersonVisaService";
import AppFormCardCollapsable from "@/components/shared/AppFormCardCollapsable.vue";
import AppCheckBox from "@/components/shared/AppCheckBox.vue";
import AppDisplayTemplateBoolean from "@/components/shared/AppDisplayTemplateBoolean.vue";
import {ToastType} from "@/services/models/shared/ToastType";
import { ApplicationRole } from '@/services/models/Role';

export default {
  components: {
    AppTablePagination,
    AppFormCardCollapsable,
    AppCheckBox,
    AppDisplayTemplateBoolean
  },
  data() {
    return {
      visas: [],
      totalVisas: 0,
      deletingRow: null,
      filterOptions: {
        page: 1,
        pageSize: 10,
        name: null,
        uin: null,
        supervisorName: null,
        onlyShowActive: true,
        employmentStatus: null,
        includeStudents: false,
        visaExpirationDate: null,
        visaTypes: null,
        employeeTypes: null,
        onlyHRProcessingVISA: false,
        excludeLawfulPermanentResidents: false,
        userPersonId: null,
        onlyHRApprovedI140: false
      },
      visaExpirationDate: null,
      isLoading: false,
      employmentStatuses: [
        {
          key: "Active",
          value: "A"
        },
        {
          key: "Deceased",
          value: "D"
        },
        {
          key: "Leave Without Pay",
          value: "L"
        },
        {
          key: "Affiliate Non-Employee",
          value: "N"
        },
        {
          key: "Retired",
          value: "R"
        },
        {
          key: "Terminated",
          value: "T"
        },
        {
          key: "Modified Service",
          value: "W"
        },
        {
          key: "Graduate Fellow",
          value: "F"
        }
      ],
      visaTypes: [
        "-",
        "AA",
        "F-1",
        "H-1",
        "J-1",
        "J-2",
        "PA",
        "PR",
        "TN",
        "XN"
      ],
      isPerformingAction: false
    };
  },
  computed: {
    readOnly() {
      return !this.hasApplicationRole(ApplicationRole.DatabaseAdministrator, ApplicationRole.HR);
    },

    employeeTypes() {
      if (this.filterOptions.includeStudents) {
        return [
          "-",
          "Student Worker",
          "Graduate Assistant",
          "Faculty",
          "Staff",
          "Temporary/Casual Staff"
        ]
      }
      return [
         "-",
        "Faculty",
        "Staff",
        "Temporary/Casual Staff"
      ]
    },

    tableFields() {
      if (!this.readOnly) {
        return [
          {
            key: "id",
            label: "",
          },
          {
            key: "name",
            label: "Name",
          },
          {
            key: "uin",
            label: "UIN",
          },
          {
            key: "organizationCode",
            label: "Department Code"
          },
          {
            key: "supervisorName",
            label: "Supervisor",
          },
          {
            key: "employmentStatus",
            label: "Employment Status",
          },
          {
            key: 'employeeType',
            label: "Employment Type"
          },
          {
            key: "visaType",
            label: "Visa Type",
          },
          {
            key: "visaExpirationDate",
            label: "Visa Expiration Date",
          },
          {
            key: "hrProcessingVISA",
            label: "Processing"
          },
          {
            key: "h1BStartDate",
            label: "H1-B Start Date"
          },
          {
            key: "hrApprovedI140",
            label: "Approved I-140"
          },
        ]
      } else {
        return [
          {
            key: "name",
            label: "Name",
          },
          {
            key: "uin",
            label: "UIN",
          },
          {
            key: "organizationCode",
            label: "Department Code"
          },
          {
            key: "supervisorName",
            label: "Supervisor",
          },
          {
            key: "employmentStatus",
            label: "Employment Status",
          },
          {
            key: "visaType",
            label: "Visa Type",
          },
          {
            key: "visaExpirationDate",
            label: "Visa Expiration Date",
          },
          {
            key: "h1BStartDate",
            label: "H1-B Start Date"
          }
        ]
      }
    }
  },
  methods: {
    loadData() {
      const vm = this;
      vm.isLoading = true;

      if (this.filterOptions.visaTypes !== null && this.filterOptions.visaTypes.length === 0) {
        this.filterOptions.visaTypes = null;
      }

      if (this.filterOptions.employeeTypes !== null && this.filterOptions.employeeTypes.length === 0) {
        this.filterOptions.employeeTypes = null;
      }

      PersonVisaService.getPeopleVisaList(this.filterOptions)
          .then((response) => {
            vm.visas = response.data;
            vm.totalVisas = response.totalCount;
          })
          .catch(() => {
            vm.redirectToError();
          })
          .finally(() => {
            vm.isLoading = false;
          });
    },

    /**
     * @description Resets the page to the first page and reloads the data
     */
    onFilterSubmit() {
      this.filterOptions.page = 1;
      this.loadData();
    },

    updateEmploymentStatus() {
      if (this.filterOptions.onlyShowActive) {
        this.filterOptions.employmentStatus = null;
      }
    },

    updateVisaExpirationDateFromButton () {
      if (this.filterOptions.visaExpirationDate !== undefined && this.filterOptions.visaExpirationDate !== null && this.filterOptions.visaExpirationDate !== '') {
        this.visaExpirationDate = this.$moment(this.filterOptions.visaExpirationDate).format("MM/DD/YYYY");
      } else {
        this.filterOptions.visaExpirationDate = null;
        this.visaExpirationDate = null;
      }
    },

    downloadCsvExport() {
      this.isPerformingAction = true;
      const vm = this;

      if (this.filterOptions.visaTypes !== null && this.filterOptions.visaTypes.length === 0) {
        this.filterOptions.visaTypes = null;
      }

      if (this.filterOptions.employeeTypes !== null && this.filterOptions.employeeTypes.length === 0) {
        this.filterOptions.employeeTypes = null;
      }

      PersonVisaService.getExcelExport(this.filterOptions)
          .catch(error => {
              vm.alert(ToastType.Danger, error.response.statusText);
          })
          .finally(() => {
              this.isPerformingAction = false;
          });
    }
  },
  created() {
    this.filterOptions.userPersonId = this.$store.getters.user.id;
    this.loadData();
  },
};
</script>
